'use client';

import React from 'react';
import { ClipboardList, FileText } from 'lucide-react';
import type { Sport, LineupInfo } from '@sharpr/types';
import { TeamLogo } from './TeamLogo';
import { PlayerAvatar } from './PlayerAvatar';
import { fullTeamNames, mlbFullTeamNames } from '../constants/teamNames';

interface NotesContentProps {
  /** Structured lineup (preferred). When present, renders the numbered batting order. */
  lineup?: LineupInfo;
  /** The team's note (rendered when no lineup is present). */
  teamNotes?: string;
  /** NBA: the analyst's note on the player, shown above the team's. */
  playerNotes?: string;
  /** The team's abbreviation: its logo and full name head the team's note. */
  teamLabel?: string;
  /** The player's name and headshot head the player's note. */
  playerName?: string;
  playerImageUrl?: string | null;
  sport: Sport;
}

function LabelledNote({ label, text }: { label: React.ReactNode; text: string }): React.ReactElement {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <p className="whitespace-pre-line">{text}</p>
    </div>
  );
}

function teamName(team: string, sport: Sport): string {
  return (sport === 'mlb' ? mlbFullTeamNames : fullTeamNames)[team] ?? team;
}

/** Sport-aware icon for a row's notes: ClipboardList for MLB, FileText for NBA. */
export function NotesIcon({ sport, className }: { sport: Sport; className?: string }): React.ReactElement {
  return sport === 'mlb'
    ? <ClipboardList className={className} />
    : <FileText className={className} />;
}

/**
 * A row's notes popover content: the MLB lineup (team logo, status, numbered order) when `lineup` is set; otherwise the
 * analyst's notes, the player's above the team's, each headed: the player's by his headshot, the team's by its logo and name.
 */
export function NotesContent({ lineup, teamNotes, playerNotes, teamLabel, playerName, playerImageUrl, sport }: NotesContentProps): React.ReactElement {
  if (!lineup) {
    return (
      <div className="space-y-3">
        {playerNotes && (
          <LabelledNote
            label={<>
              {/* The row's 28px headshot, drawn at 20px: sharp on retina, and on desktop the row's cached copy. */}
              {playerName && <PlayerAvatar name={playerName} imageUrl={playerImageUrl} size="sm" className="w-5 h-5" />}
              <span>Player</span>
            </>}
            text={playerNotes}
          />
        )}
        {teamNotes && (
          <LabelledNote
            label={teamLabel ? <>
              <TeamLogo team={teamLabel} sport={sport} className="w-4 h-4" />
              <span>{teamName(teamLabel, sport)}</span>
            </> : 'Team'}
            text={teamNotes}
          />
        )}
      </div>
    );
  }

  const isProjected = lineup.status === 'Projected';

  return (
    <>
      <div className={`flex items-center gap-1.5 font-semibold mb-1.5 ${isProjected ? 'text-amber-600 dark:text-amber-400' : 'text-fg dark:text-text-primary'}`}>
        <TeamLogo team={lineup.team} sport={sport} className="w-4 h-4" />
        <span>{lineup.status} lineup</span>
      </div>
      {lineup.names.map((name, i) => (
        <div key={i} className="text-xs leading-5">
          <span className="text-muted-foreground w-4 inline-block">{i + 1}.</span> {name}
        </div>
      ))}
    </>
  );
}

/** Returns true if the lineup is the projected (unconfirmed) status. */
export function isProjectedLineup(lineup: LineupInfo | undefined): boolean {
  return lineup?.status === 'Projected';
}
